/**
 * Homepage featured-carousel tile — content (overlay caption)
 * vs product (native-ratio media, brand + name below).
 *
 * Mobile: the whole card is a real `<a>` when an outbound URL exists so the
 * full post is tappable (window.open-only handlers are blocked on touch,
 * leaving only the Shop badge clickable).
 */

import React, { useRef } from "react";
import { addHttpToLink } from "../../../Utils/StayUpToDate/RenderComponent";
import { FeaturedCarouselCardMedia } from "./FeaturedCarouselCardMedia";
import {
  syncProductCardMaxHeightBg,
  useProductCardMaxHeightBg,
} from "./templatePages/ShopProductCatalogPanel";
import type { FeaturedCarouselItem } from "./types";
import { sanitizePublicCardTitle } from "../../../Utils/media/feedCardTitle";

type CardItem = FeaturedCarouselItem;

function isProductItem(it: CardItem): boolean {
  return (
    it.kind === "shop" ||
    String(it.postType || "")
      .trim()
      .toUpperCase() === "PRODUCT"
  );
}

export type FeaturedCarouselCardProps = {
  item: CardItem;
  index?: number;
  /** Marquee duplicate track uses the same card chrome. */
  marquee?: boolean;
  /** Hide from AT (marquee visual duplicate). */
  ariaHidden?: boolean;
  stillOnly?: boolean;
  isSquareMedia?: boolean;
  isMobile?: boolean;
  interactive?: boolean;
  onActivate?: () => void;
  /** @deprecated Mobile tap-to-reveal removed — whole card navigates. */
  mobileActionRevealed?: boolean;
  /** @deprecated Mobile tap-to-reveal removed — whole card navigates. */
  onMobileCardTap?: () => void;
  /** Hover-play mute control — mirrors carousel `content_sound`. */
  soundOn?: boolean;
};

export function FeaturedCarouselCard({
  item,
  index = 0,
  marquee = false,
  ariaHidden = false,
  stillOnly = false,
  isSquareMedia = false,
  isMobile = false,
  interactive = false,
  onActivate,
  soundOn = true,
}: FeaturedCarouselCardProps) {
  const product = isProductItem(item);
  const displayTitle = sanitizePublicCardTitle(item.title, {
    type: item.postType,
    product_name: item.title,
    caption: item.title,
  });
  const mediaShellRef = useRef<HTMLDivElement | null>(null);
  // Same priority as activateFeaturedCarouselItem: shop → permalink.
  const externalHref = addHttpToLink(
    String(item.shopLink || item.permalink || "").trim(),
  );
  // Prefer explicit label from resolveShopButtonCta — never assume Shop.
  const contentShopLabel = String(item.shopCtaLabel || "").toUpperCase();
  const cardInteractive = Boolean(interactive);
  // Prefer a real link for outbound URLs so mobile taps aren't popup-blocked.
  const useAnchor = cardInteractive && Boolean(externalHref);

  useProductCardMaxHeightBg(mediaShellRef, product, [
    product,
    item.image,
    item.productImage,
    item.id,
  ]);

  const rootClass = [
    marquee ? "mte-tpl-fc-marquee-card" : "mte-tpl-fc-card",
    // Same product chrome for peek + marquee — marquee only swaps the strip shell.
    product ? "mte-tpl-fc-card--product" : "",
    !product && isSquareMedia
      ? marquee
        ? "mte-tpl-fc-marquee-card--square"
        : "mte-tpl-fc-card--square"
      : "",
    cardInteractive
      ? ""
      : marquee
        ? "mte-tpl-fc-marquee-card--static"
        : "mte-tpl-fc-card--static",
  ]
    .filter(Boolean)
    .join(" ");

  const blurFinePointerTarget = (el: HTMLElement | null) => {
    if (
      !el ||
      typeof window === "undefined" ||
      !window.matchMedia("(hover: hover) and (pointer: fine)").matches
    ) {
      return;
    }
    el.blur();
  };

  const shopPill = product ? (
    <>
      {!isMobile ? (
        <div className="mte-tpl-shop-media-scrim" aria-hidden />
      ) : null}
      {/* Decorative badge — card itself is the link (nested <a> is invalid). */}
      <span
        className={`mte-tpl-shop-pill mte-tpl-shop-pill--product${
          isMobile ? " mte-tpl-shop-pill--mobile" : ""
        }`}
        aria-hidden
      >
        SHOP
      </span>
    </>
  ) : null;

  const contentShopPill =
    !product &&
    Boolean(String(item.shopLink || "").trim()) &&
    Boolean(contentShopLabel) ? (
      <>
        {!isMobile ? (
          <div className="mte-tpl-shop-media-scrim" aria-hidden />
        ) : null}
        <span
          className={`mte-tpl-shop-pill${
            isMobile ? " mte-tpl-shop-pill--mobile" : ""
          }`}
          aria-hidden
        >
          {contentShopLabel}
        </span>
      </>
    ) : null;

  const handleCardPress = (event?: React.SyntheticEvent) => {
    onActivate?.();
    const t = event?.currentTarget;
    if (t instanceof HTMLElement) blurFinePointerTarget(t);
  };

  const accessibleName =
    String(item.title || item.brand || "").trim() ||
    (product ? "Open product" : "Open post");

  const mediaBlock = product ? (
    <>
      <div ref={mediaShellRef} className="mte-tpl-fc-media-shell">
        <FeaturedCarouselCardMedia
          item={item}
          priority={!ariaHidden && index < 8}
          stillOnly
          deferLoad={ariaHidden}
          soundOn={soundOn}
          variant="product"
          onMediaAspectRatio={() => {
            const shell = mediaShellRef.current;
            const img = shell?.querySelector(
              "img",
            ) as HTMLImageElement | null;
            syncProductCardMaxHeightBg(shell, img);
          }}
        />
        {shopPill}
      </div>
      <div className="mte-tpl-fc-product-meta">
        {item.brand ? (
          <div className="mte-tpl-fc-p-brand" title={item.brand}>
            {item.brand}
          </div>
        ) : null}
        {displayTitle ? (
          <div className="mte-tpl-fc-p-name" title={displayTitle}>
            {displayTitle}
          </div>
        ) : null}
      </div>
    </>
  ) : (
    <>
      <FeaturedCarouselCardMedia
        item={item}
        priority={!ariaHidden && index < 8}
        stillOnly={stillOnly}
        // Marquee loop duplicates the track — don't eager-fetch the copy.
        deferLoad={ariaHidden}
        soundOn={soundOn}
        variant="content"
      />
      {item.isText ? null : <div className="mte-tpl-fc-grad" />}
      {!item.isText && displayTitle ? (
        <div className="mte-tpl-fc-title">{displayTitle}</div>
      ) : null}
      {shopPill}
      {contentShopPill}
    </>
  );

  if (useAnchor) {
    return (
      <a
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaHidden ? undefined : accessibleName}
        aria-hidden={ariaHidden ? true : undefined}
        tabIndex={ariaHidden ? -1 : undefined}
        onClick={(event) => {
          // Tracking / analytics only — navigation is the href.
          onActivate?.();
          blurFinePointerTarget(event.currentTarget);
        }}
        className={rootClass}
        data-fc-kind={product ? "product" : "content"}
      >
        {mediaBlock}
      </a>
    );
  }

  const isAriaButton = !ariaHidden && cardInteractive;

  return (
    <div
      role={isAriaButton ? "button" : undefined}
      tabIndex={isAriaButton ? 0 : undefined}
      aria-label={isAriaButton ? accessibleName : undefined}
      aria-hidden={ariaHidden ? true : undefined}
      onClick={
        isAriaButton
          ? (event) => handleCardPress(event)
          : undefined
      }
      onKeyDown={
        isAriaButton
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardPress(e);
              }
            }
          : undefined
      }
      className={rootClass}
      data-fc-kind={product ? "product" : "content"}
    >
      {mediaBlock}
    </div>
  );
}
